import PageLoader from '@components/PageLoader'
import { useAdminUser } from '@hooks/useAdminUser'
import React from 'react'
import { useParams } from 'react-router-dom'
import UserAppEvents from './UserAppEvents'
import { formatDate, formatName } from '../adminFormat'
import {
	BackLink,
	Card,
	CardTitle,
	DeletedBadge,
	DetailHeader,
	DetailTitle,
	Field,
	FieldGrid,
	FieldLabel,
	FieldValue,
	RelatedList,
	RelatedMeta,
	RelatedRow,
} from '../adminDetail.styles'
import { EmptyNoteInline } from '@components/EmptyState'
import { ErrorNote } from '@components/Notice'

/**
 * Read-only admin detail for one account: profile + PII, lifecycle counts, and the
 * trips it belongs to (each links to that trip's admin detail). No mutations.
 */
const UserDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const { data: user, isLoading, isError } = useAdminUser(Number(id))

	return (
		<>
			<BackLink to="/admin/users">← Back to users</BackLink>

			{isLoading ? (
				<PageLoader label="Loading user…" />
			) : isError || !user ? (
				<ErrorNote>Could not load this user.</ErrorNote>
			) : (
				<>
					<DetailHeader>
						<DetailTitle>{formatName(user.firstName, user.lastName)}</DetailTitle>
						{user.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
					</DetailHeader>

					<Card>
						<CardTitle>Account</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Email</FieldLabel>
								<FieldValue>{user.email}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Phone</FieldLabel>
								<FieldValue>{user.phone ?? '—'}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Role</FieldLabel>
								<FieldValue>{user.role}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Registration source</FieldLabel>
								<FieldValue>{user.registrationSource}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Payment method</FieldLabel>
								<FieldValue>{user.paymentMethod}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Signed up</FieldLabel>
								<FieldValue>{formatDate(user.signupDate)}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					<Card>
						<CardTitle>Activity</CardTitle>
						<FieldGrid>
							<Field>
								<FieldLabel>Trips</FieldLabel>
								<FieldValue>{user.tripCount.toLocaleString()}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Expenses created</FieldLabel>
								<FieldValue>{user.expensesCreatedCount.toLocaleString()}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Events created</FieldLabel>
								<FieldValue>{user.eventsCreatedCount.toLocaleString()}</FieldValue>
							</Field>
							<Field>
								<FieldLabel>Ideas created</FieldLabel>
								<FieldValue>{user.ideasCreatedCount.toLocaleString()}</FieldValue>
							</Field>
						</FieldGrid>
					</Card>

					<Card>
						<CardTitle>Trips ({user.trips.length})</CardTitle>
						{user.trips.length === 0 ? (
							<EmptyNoteInline>This user is not part of any trips.</EmptyNoteInline>
						) : (
							<RelatedList>
								{user.trips.map(trip => (
									<RelatedRow key={trip.id} to={`/admin/trips/${trip.id}`} $deleted={trip.isDeleted}>
										<span>
											{trip.name || 'Untitled trip'}
											{trip.isDeleted && ' · deleted'}
										</span>
										<RelatedMeta>{trip.isOwner ? 'Owner' : 'Member'}</RelatedMeta>
									</RelatedRow>
								))}
							</RelatedList>
						)}
					</Card>

					<UserAppEvents userId={user.id} />
				</>
			)}
		</>
	)
}

export default UserDetailPage
